import React, { useState, useEffect } from 'react';
import { personalInfo } from '../mock';
import { Mail, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Contact = () => {
  // Initialize EmailJS on component mount
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    }
  }, []);
  const { language } = useLanguage();
  const t = translations[language];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      
      if (!publicKey || !serviceId || !templateId) {
        console.warn('EmailJS configuration missing.', { serviceId, templateId, publicKey: !!publicKey });
        throw new Error('Email service not configured');
      }

      const templateParams = {
        title: formData.subject,
        name: formData.name,
        email: formData.email,
        message: formData.message,
        to_mail: 'oktaybahadir51@gmail.com',
        time: new Date().toLocaleString('tr-TR'),
      };

      console.log('Sending email with:', { serviceId, templateId, templateParams });

      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      console.log('EmailJS Response:', response);

      toast({
        title: t.contact.success,
        description: t.contact.successDesc,
      });
      
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      let errorMessage = t.contact.errorDesc;
      if (error.text) {
        errorMessage = error.text;
        console.error('EmailJS Error Details:', error.text);
      }
      toast({
        title: t.contact.error,
        description: errorMessage,
        variant: "destructive"
      });
      console.error('EmailJS Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
            {t.contact.title}
          </h2>
          <div className="w-24 h-1 bg-gray-900 mx-auto mb-6"></div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            {t.contact.description}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-gray-50 p-8 md:p-12 border-2 border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-base font-medium text-gray-900 mb-2 block">
                  {t.contact.name}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-gray-300 focus:border-gray-900 focus:ring-gray-900"
                  placeholder={t.contact.namePlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-base font-medium text-gray-900 mb-2 block">
                  {t.contact.email}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-gray-300 focus:border-gray-900 focus:ring-gray-900"
                  placeholder={t.contact.emailPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="subject" className="text-base font-medium text-gray-900 mb-2 block">
                  {t.contact.subject}
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-gray-300 focus:border-gray-900 focus:ring-gray-900"
                  placeholder={t.contact.subjectPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-base font-medium text-gray-900 mb-2 block">
                  {t.contact.message}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-3 border-gray-300 focus:border-gray-900 focus:ring-gray-900 resize-none"
                  placeholder={t.contact.messagePlaceholder}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 text-white hover:bg-gray-800 py-6 text-base font-medium transition-all duration-300 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  t.contact.sending
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {t.contact.send}
                  </>
                )}
              </Button>
            </form>
          </div>

          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <Mail className="w-5 h-5" />
              <a href={`mailto:${personalInfo.email}`} className="hover:text-gray-900 transition-colors">
                {personalInfo.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;